import { Restaurant } from '../models';

/** 进入主框架时，用接口结果或本地缓存解析 STAFF 可选餐厅与当前店 */
export type ResolveStaffRestaurantSelectionInput = {
    /** 重拉员工详情成功时的绑定列表；null 表示接口失败，应沿用缓存 */
    refreshedBelongedRestaurant: Restaurant[] | null;
    /** localStorage ACCOUNT 中的绑定餐厅，接口失败或字段缺失时回退 */
    cachedBelongedRestaurant: Restaurant[];
    /** 上次选中的餐厅，仍在新列表中则保留 */
    lastSelectedRestaurant?: Restaurant | null;
};

/** 解析后的餐厅列表、当前店，以及是否因全部解绑需踢出登录 */
export type ResolveStaffRestaurantSelectionResult = {
    /** 顶栏下拉使用的绑定餐厅列表 */
    belongedRestaurant: Restaurant[];
    /** 当前聚焦餐厅；无绑定时为空对象 */
    currentRestaurant: Restaurant;
    /** 接口成功且剔除无效项后列表为空时为 true；接口失败为 false */
    shouldLogoutBecauseUnbound: boolean;
};

/**
 * 绑定项是否可用于顶栏与业务页：必须有非空 `string_id`。
 * 仅含 `_id` 的占位对象视为无效。
 * @param restaurant - 绑定列表中的一项
 * @returns 有餐厅码时为 true
 */
export function isValidBelongedRestaurant(restaurant: Restaurant): boolean {
    return Boolean(String(restaurant.string_id ?? '').trim());
}

/**
 * 去掉仅有 `_id`、没有 `string_id` 的无效绑定。
 * @param restaurants - 原始绑定列表
 * @returns 可用于选店的列表
 */
export function filterValidBelongedRestaurants(restaurants: Restaurant[]): Restaurant[] {
    return restaurants.filter(isValidBelongedRestaurant);
}

/**
 * 两家店是否同一家：优先比 string_id。
 * @param left - 左侧餐厅
 * @param right - 右侧餐厅
 * @returns 视为同一家时为 true
 */
export function isSameRestaurant(left?: Restaurant | null, right?: Restaurant | null): boolean {
    if (!left || !right) {
        return false;
    }
    const leftStringId = String(left.string_id ?? '').trim();
    const rightStringId = String(right.string_id ?? '').trim();
    if (leftStringId && rightStringId) {
        return leftStringId === rightStringId;
    }
    return false;
}

/**
 * 从绑定列表与上次选中店解析当前餐厅：仍在列表则保留，否则取第一家，空列表返回 {}。
 * @param belongedRestaurant - 当前账号绑定的餐厅
 * @param lastSelectedRestaurant - 本地记住的上次选中店
 * @returns 应设为当前店的餐厅对象
 */
export function resolveCurrentRestaurantFromBelonged(
    belongedRestaurant: Restaurant[],
    lastSelectedRestaurant?: Restaurant | null,
): Restaurant {
    if (!belongedRestaurant.length) {
        return {};
    }
    if (lastSelectedRestaurant) {
        const stillBound = belongedRestaurant.find((item) => isSameRestaurant(item, lastSelectedRestaurant));
        if (stillBound) {
            return stillBound;
        }
    }
    return belongedRestaurant[0] || {};
}

/**
 * 刷新页后得到下拉列表与当前店：接口成功以服务端为准并剔除无效项；失败用过滤后的缓存。
 * @param input - 接口结果、缓存列表、上次选中店
 * @returns 列表、当前店、是否因无有效绑定而踢出登录
 */
export function resolveStaffRestaurantSelection(
    input: ResolveStaffRestaurantSelectionInput,
): ResolveStaffRestaurantSelectionResult {
    const { refreshedBelongedRestaurant, cachedBelongedRestaurant, lastSelectedRestaurant } = input;
    const usedServerList = Array.isArray(refreshedBelongedRestaurant);
    const rawList = usedServerList
        ? refreshedBelongedRestaurant
        : (cachedBelongedRestaurant || []);
    const belongedRestaurant = filterValidBelongedRestaurants(rawList);
    // 接口成功且剔除无效项后没有可用店，视为无绑定
    const shouldLogoutBecauseUnbound = usedServerList && belongedRestaurant.length === 0;
    const currentRestaurant = resolveCurrentRestaurantFromBelonged(
        belongedRestaurant,
        lastSelectedRestaurant,
    );
    return {
        belongedRestaurant,
        currentRestaurant,
        shouldLogoutBecauseUnbound,
    };
}
